import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, CheckCircle2, CircleDashed, KeyRound, LogOut, Mail, MailPlus, MessageCircle, Send, Smartphone } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import AuthShell from '../../components/auth/AuthShell';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { getEmployerOnboardingPath } from '../../utils/employerOnboarding';

const inputBase =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.error || error?.response?.data?.message || error?.message || error?.error || fallback
);

const isInternationalPhone = (phone) => /^\+[1-9]\d{7,14}$/.test(String(phone || '').trim());

function ContactVerificationPage() {
  const { user, verificationStatus, logout, setToken, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const [status, setStatus] = useState({
    email: user?.email || '',
    phone: user?.phone || '',
    emailVerifiedAt: user?.emailVerifiedAt || null,
    phoneVerifiedAt: user?.phoneVerifiedAt || null,
    contactVerificationComplete: Boolean(user?.emailVerifiedAt && user?.phoneVerifiedAt)
  });
  const [emailCode, setEmailCode] = useState('');
  const [phone, setPhone] = useState(user?.phone || '');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [statusError, setStatusError] = useState('');

  const emailVerified = Boolean(status.emailVerifiedAt);

  const applyStatus = useCallback((nextStatus) => {
    setStatus(nextStatus);
    setPhone((currentPhone) => currentPhone || nextStatus.phone || '');
    updateUser({
      email: nextStatus.email,
      phone: nextStatus.phone,
      emailVerifiedAt: nextStatus.emailVerifiedAt,
      phoneVerifiedAt: nextStatus.phoneVerifiedAt
    });
  }, [updateUser]);

  const loadStatus = useCallback(async () => {
    try {
      setStatusError('');
      const nextStatus = await authAPI.getContactVerificationStatus();
      applyStatus(nextStatus);
      return nextStatus;
    } catch (error) {
      setStatusError(getErrorMessage(error, 'Could not refresh your verification progress.'));
      throw error;
    } finally {
      setLoadingStatus(false);
    }
  }, [applyStatus]);

  useEffect(() => {
    loadStatus().catch(() => {});
  }, [loadStatus]);

  useEffect(() => {
    if (!status.contactVerificationComplete) return;

    navigate(getEmployerOnboardingPath({
      ...user,
      emailVerifiedAt: status.emailVerifiedAt,
      phoneVerifiedAt: status.phoneVerifiedAt
    }, verificationStatus), { replace: true });
  }, [navigate, status, user, verificationStatus]);

  useEffect(() => {
    if (!resendCooldown) return undefined;

    const timeout = window.setTimeout(() => {
      setResendCooldown((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [resendCooldown]);

  const handleVerifyEmail = async (event) => {
    event.preventDefault();
    if (emailCode.length !== 6 || busyAction) return;

    try {
      setBusyAction('email');
      const response = await authAPI.verifyEmail({ email: status.email, code: emailCode });
      if (response.token || response.accessToken) {
        setToken(response.token || response.accessToken, response.refreshToken);
      }
      setEmailCode('');
      await loadStatus();
      toast.success('Email address verified.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not verify that email code.'));
    } finally {
      setBusyAction('');
    }
  };

  const handleResendEmail = async () => {
    if (busyAction || resendCooldown) return;

    try {
      setBusyAction('resend-email');
      await authAPI.resendEmailVerification({ email: status.email });
      setResendCooldown(45);
      toast.success('A new email code was sent.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not resend the email code.'));
    } finally {
      setBusyAction('');
    }
  };

  const handleRequestPhoneCode = async () => {
    const normalizedPhone = phone.trim();
    if (!isInternationalPhone(normalizedPhone)) {
      toast.error('Enter a valid international phone number, including the + country code.');
      return;
    }

    try {
      setBusyAction('send-phone');
      const response = await authAPI.requestPhoneVerification({ phone: normalizedPhone });
      setPhone(response.phone || normalizedPhone);
      setPhoneCodeSent(true);
      setPhoneCode('');
      setResendCooldown(45);
      toast.success('WhatsApp verification code sent.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not send the WhatsApp verification code.'));
    } finally {
      setBusyAction('');
    }
  };

  const handleVerifyPhone = async (event) => {
    event.preventDefault();
    if (phoneCode.length !== 6 || busyAction) return;

    try {
      setBusyAction('phone');
      await authAPI.verifyPhone({ phone: phone.trim(), code: phoneCode });
      await loadStatus();
      toast.success('WhatsApp number verified.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not verify that WhatsApp code.'));
    } finally {
      setBusyAction('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (loadingStatus) {
    return (
      <AuthShell title="Checking your account" subtitle="Loading your saved verification progress.">
        <div className="flex items-center gap-3 rounded-md border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-medium text-slate-600">
          <CircleDashed className="h-4 w-4 animate-spin text-sky-600" />
          Loading verification progress
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow={`Employer account setup - Step ${emailVerified ? '2' : '1'} of 2`}
      title={emailVerified ? 'Confirm your WhatsApp number' : 'Verify your work email'}
      subtitle={emailVerified
        ? 'We use WhatsApp OTP verification to secure your employer account before company verification.'
        : 'Enter the 6 digit code sent to your work email before continuing.'}
    >
      <div className="mb-6 flex gap-2" aria-label={`Step ${emailVerified ? '2' : '1'} of 2`}>
        <span className="h-1.5 flex-1 rounded-full bg-sky-600" />
        <span className={`h-1.5 flex-1 rounded-full ${emailVerified ? 'bg-sky-600' : 'bg-slate-200'}`} />
      </div>

      {statusError && (
        <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {statusError}
        </div>
      )}

      {!emailVerified ? (
        <form onSubmit={handleVerifyEmail} className="space-y-5">
          <div className="rounded-lg border border-sky-100 bg-[#eef9ff] px-4 py-3 text-sm leading-6 text-slate-700">
            Check <span className="font-semibold text-[#0b1f3a]">{status.email}</span> for your account verification code.
          </div>

          <div>
            <label htmlFor="email-code" className="mb-2 block text-sm font-medium text-slate-700">
              Email verification code
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input
                id="email-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={emailCode}
                onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`${inputBase} pl-10 tracking-[0.35em]`}
                placeholder="000000"
                autoFocus
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={emailCode.length !== 6 || Boolean(busyAction)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busyAction === 'email' ? 'Verifying...' : 'Verify email and continue'}
            <ArrowRight className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={handleResendEmail}
            disabled={Boolean(busyAction) || resendCooldown > 0}
            className="inline-flex w-full items-center justify-center gap-2 text-sm font-medium text-sky-700 transition hover:text-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <MailPlus className="h-4 w-4" />
            {resendCooldown ? `Resend code in ${resendCooldown}s` : 'Resend email code'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyPhone} className="space-y-5">
          <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <span className="inline-flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              Email verified
            </span>
          </div>

          <div>
            <label htmlFor="whatsapp-phone" className="mb-2 block text-sm font-medium text-slate-700">
              WhatsApp number
            </label>
            <div className="relative">
              <Smartphone className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input
                id="whatsapp-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  setPhoneCodeSent(false);
                  setPhoneCode('');
                }}
                className={`${inputBase} pl-10`}
                placeholder="+254712345678"
                autoFocus
                required
              />
            </div>
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Use international format with the + country code. You can correct your signup number here.
            </p>
          </div>

          {phoneCodeSent && (
            <div>
              <label htmlFor="phone-code" className="mb-2 block text-sm font-medium text-slate-700">
                WhatsApp verification code
              </label>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="phone-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={phoneCode}
                  onChange={(event) => setPhoneCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${inputBase} pl-10 tracking-[0.35em]`}
                  placeholder="000000"
                  autoFocus
                  required
                />
              </div>
            </div>
          )}

          {phoneCodeSent ? (
            <>
              <button
                type="submit"
                disabled={phoneCode.length !== 6 || Boolean(busyAction)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busyAction === 'phone' ? 'Verifying...' : 'Verify number and continue'}
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleRequestPhoneCode}
                disabled={Boolean(busyAction) || resendCooldown > 0}
                className="inline-flex w-full items-center justify-center gap-2 text-sm font-medium text-sky-700 transition hover:text-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {resendCooldown ? `Resend code in ${resendCooldown}s` : 'Resend WhatsApp code'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleRequestPhoneCode}
              disabled={Boolean(busyAction)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busyAction === 'send-phone' ? 'Sending...' : 'Send WhatsApp code'}
              <MessageCircle className="h-4 w-4" />
            </button>
          )}
        </form>
      )}

      <div className="mt-6 border-t border-slate-200 pt-5 text-center">
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center justify-center gap-2 text-sm font-medium text-slate-500 transition hover:text-[#0b1f3a]"
        >
          <LogOut className="h-4 w-4" />
          Sign out and continue later
        </button>
      </div>
    </AuthShell>
  );
}

export default ContactVerificationPage;
